import { useState, useEffect } from 'react';
import { trackContentEvent } from '@/analytics/analytics';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ExternalLink, Clock, Loader2, Maximize2, Minimize2, FlaskConical, Wrench, BookOpen, Book, ChevronDown, ChevronUp, Sparkles, Play, Headphones } from 'lucide-react';
import BudleeAIIcon from '@/components/BudleeAIIcon';
import { curiosityAPI } from '../services/curiosityAPI';
import { getContentThumbnail } from '@/utils/thumbnailUtils';
import BudleeAIChatPanel from '@/components/BudleeAIChatPanel';
import { useSubjectChapter } from '@/context/SubjectChapterContext';
import StatsBar from '@/components/StatsBar';
import { useRef } from 'react';

// Mock data generator
const generateMockContent = (contentId, contentType) => {
  const baseContent = {
    id: contentId,
    title: `Sample ${contentType.charAt(0).toUpperCase() + contentType.slice(1)} Content`,
    description: `This is a comprehensive ${contentType} resource designed to help you learn and understand complex concepts through interactive experiences.`,
    content_type: contentType,
    thumbnail_url: null, // Will use getContentThumbnail fallback
    tag_type: 'core',
    is_featured: false,
    is_active: true,
    status: 'published',
  };

  switch (contentType) {
    case 'simulation':
      return {
        ...baseContent,
        title: 'Interactive Physics Simulation',
        description: 'Explore the principles of motion and forces through this interactive simulation. Adjust parameters and observe real-time results.',
        simulation_embed_url: 'https://phet.colorado.edu/sims/html/forces-and-motion-basics/latest/forces-and-motion-basics_en.html',
        external_url: 'https://phet.colorado.edu/sims/html/forces-and-motion-basics/latest/forces-and-motion-basics_en.html',
      };
    case 'tool':
      return {
        ...baseContent,
        title: 'Advanced Calculator Tool',
        description: 'A powerful calculation tool for solving complex mathematical problems with step-by-step solutions.',
        external_url: 'https://www.wolframalpha.com/',
      };
    case 'course':
      return {
        ...baseContent,
        title: 'Complete Course: Advanced Concepts',
        description: 'A comprehensive course covering all aspects of the topic with video lessons, quizzes, and assignments.',
        external_url: 'https://www.khanacademy.org/',
        course_duration_hours: 10,
      };
    case 'book':
      return {
        ...baseContent,
        title: 'Reference Guide: Complete Textbook',
        description: 'An in-depth reference book covering all topics with detailed explanations, examples, and practice problems.',
        external_url: 'https://openstax.org/',
        read_time_minutes: 120,
      };
    default:
      return baseContent;
  }
};

/**
 * Content Detail View
 * Path: /curiosity/content/{contentId}
 * 
 * For Simulations:
 * - Embedded iframe with simulation
 * - Full-screen button
 * - Description panel (collapsible)
 * 
 * For Tools/Courses/Books:
 * - Content details (title, description, metadata)
 * - Large "Open Tool" / "Start Course" / "Read Now" button
 * - Opens external_url in new tab
 * - Track "clicked" interaction
 */
export default function ContentDetail() {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showDescription, setShowDescription] = useState(true);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [prefilledText, setPrefilledText] = useState('');
  const [transcriptText, setTranscriptText] = useState('');
  const { selectedSubjectId, selectedChapterId } = useSubjectChapter();
  const timeSpentIntervalRef = useRef(null);

  const isSmallScreen = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches;

  useEffect(() => {
    if (contentId) {
      loadContent();
    }
    try {
      if (timeSpentIntervalRef.current) clearInterval(timeSpentIntervalRef.current);
      timeSpentIntervalRef.current = setInterval(() => {
        try {
          trackContentEvent('page_time_spent', {
            page: 'content_detail',
            content_id: contentId ? parseInt(contentId) : null,
            content_type: content?.content_type || null,
            time_spent_seconds: 15,
          });
        } catch (e) {}
      }, 15000);
    } catch (e) {}

    return () => {
      try { if (timeSpentIntervalRef.current) clearInterval(timeSpentIntervalRef.current); } catch (e) {}
    };
  }, [contentId]);

  const loadContent = async () => {
    try {
      setLoading(true);

      const contentData = await curiosityAPI.getContent(contentId);
      setContent(contentData);

      // Load transcript if available for RAG context
      if (contentData.transcript?.transcript_text) {
        setTranscriptText(contentData.transcript.transcript_text);
      }

      // Redirect podcast content to AudioPlayer
      if (contentData.content_type === 'podcast') {
        navigate(`/student/curiosity/podcast/${contentId}`, { replace: true });
        return;
      }

      // Redirect video content to VideoPlayer
      if (contentData.content_type === 'video') {
        navigate(`/student/curiosity/video/${contentId}`, { replace: true });
        return;
      }

      // Track play/view for embedded simulations and tools when opened
      try {
        if (contentData.content_type === 'simulation' || contentData.content_type === 'tool' || contentData.content_type === 'course' || contentData.content_type === 'book') {
          trackContentEvent('content_play', {
            content_id: contentId ? parseInt(contentId) : null,
            content_type: contentData.content_type,
            content_title: contentData.title || null,
            subject_id: contentData.chapter_detail?.subject_id || selectedSubjectId || null,
            chapter_id: contentData.chapter_detail?.chapter_id || selectedChapterId || null,
          });
        }
      } catch (e) {}

      // Track "viewed" interaction (non-blocking)
      curiosityAPI.recordInteraction({
        content: parseInt(contentId),
        interaction_type: 'viewed',
      }).catch(err => console.warn('Failed to record interaction:', err));
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenExternal = async () => {
    if (!content?.external_url) return;

    // Track "clicked" interaction (non-blocking)
    curiosityAPI.recordInteraction({
      content: parseInt(contentId),
      interaction_type: 'clicked',
    }).catch(err => console.warn('Failed to record interaction:', err));

    // Open in new tab
    try {
      trackContentEvent('content_play', {
        content_id: contentId ? parseInt(contentId) : null,
        content_type: content?.content_type || 'external',
        content_title: content?.title || null,
        subject_id: content?.chapter_detail?.subject_id || selectedSubjectId || null,
        chapter_id: content?.chapter_detail?.chapter_id || selectedChapterId || null,
      });
    } catch (e) {}

    window.open(content.external_url, '_blank', 'noopener,noreferrer');
  };

  const getContentIcon = (contentType) => {
    switch (contentType) {
      case 'simulation':
        return FlaskConical;
      case 'tool':
        return Wrench;
      case 'course':
        return BookOpen;
      case 'book':
        return Book;
      default:
        return ExternalLink;
    }
  };

  const getActionButtonText = (contentType) => {
    switch (contentType) {
      case 'simulation':
        return 'Open Simulation';
      case 'tool':
        return 'Try Tool';
      case 'course':
        return 'Start Course';
      case 'book':
        return 'Read Now';
      default:
        return 'Open';
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleAskBudleeAI = () => {
    // Set transcript as prefilled text for RAG context
    if (transcriptText) {
      setPrefilledText(transcriptText);
    } else if (content?.description) {
      // Fallback to description if no transcript
      setPrefilledText(content.description);
    }
    setShowChatPanel(true);
  };

  const handleCloseChatPanel = () => {
    setShowChatPanel(false);
    setPrefilledText('');
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-y-auto">
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--green-bright))]" />
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-y-auto">
        <div className="max-w-4xl mx-auto w-full px-6 py-12">
          <div className="text-center space-y-4">
            <p className="text-muted-foreground text-lg">Content not found</p>
            <Button
              onClick={() => navigate(-1)}
              className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))]"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const Icon = getContentIcon(content.content_type);
  const isSimulation = content.content_type === 'simulation';
  const embedUrl = content.simulation_embed_url || content.external_url;

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-hidden relative">
      {/* Mobile StatsBar - Show at top on mobile only */}
      <div className="flex-shrink-0 pt-2 pb-2 flex justify-center lg:hidden">
        <div className="w-fit">
          <StatsBar />
        </div>
      </div>

      {/* Main Content Container */}
      <div className="flex flex-1 h-full bg-[hsl(var(--main-bg))] overflow-hidden relative">

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col overflow-y-auto transition-all duration-300 ${!isSmallScreen && showChatPanel ? 'pr-[400px]' : ''} ${isSmallScreen && showChatPanel ? 'h-1/2' : ''}`}>
        <div className="max-w-7xl mx-auto w-full px-6 py-6 space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/student/curiosity')}
              className="text-foreground hover:bg-[hsl(var(--card-bg))] hover:text-foreground"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Curiosity Centre
            </Button>
          </div>

          {/* Simulation: Embedded iframe - Shown First */}
          {isSimulation && embedUrl && (
            <div className="space-y-4">
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] overflow-hidden">
                <CardContent className="p-0">
                  <div className="relative">
                    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-[hsl(var(--main-bg))]' : 'relative'} transition-all`}>
                      <div className={`${isFullscreen ? 'h-screen' : 'aspect-video'} relative`}>
                        <iframe
                          src={embedUrl}
                          className="w-full h-full border-0"
                          allow="fullscreen"
                          allowFullScreen
                          title={content.title}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={toggleFullscreen}
                          className="absolute top-4 right-4 bg-[hsl(var(--card-bg))]/90 hover:bg-[hsl(var(--card-bg))] backdrop-blur-sm"
                        >
                          {isFullscreen ? (
                            <>
                              <Minimize2 className="h-4 w-4 mr-2" />
                              Exit Fullscreen
                            </>
                          ) : (
                            <>
                              <Maximize2 className="h-4 w-4 mr-2" />
                              Fullscreen
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tool/Course/Book: Thumbnail - Shown First */}
          {!isSimulation && (
            <div className="space-y-4">
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] overflow-hidden">
                <CardContent className="p-0">
                  <div className="aspect-video relative">
                    <img
                      src={getContentThumbnail(content, 1280, 720)}
                      alt={content.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to placeholder if image fails to load
                        e.target.src = getContentThumbnail({ content_type: content.content_type }, 1280, 720);
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Title and Description - Matching Screenshot Style */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {content.tag_type && (
                <Badge
                  variant={content.tag_type === 'core' ? 'default' : 'secondary'}
                  className="px-3 py-1"
                >
                  {content.tag_type === 'core' ? 'CORE' : 'GO DEEPER'}
                </Badge>
              )}
              <Badge variant="outline" className="px-3 py-1 capitalize">
                {content.content_type}
              </Badge>
              {(content.course_duration_hours || content.read_time_minutes) && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">
                    {content.course_duration_hours
                      ? `${content.course_duration_hours} hrs`
                      : content.read_time_minutes
                        ? `${content.read_time_minutes} min`
                        : ''}
                  </span>
                </div>
              )}
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold text-foreground">{content.title}</h1>
            {content.description && (
              <p className="text-muted-foreground text-xs sm:text-sm lg:text-lg max-w-3xl">{content.description}</p>
            )}
          </div>

          {/* Mobile: Quick Actions - Show above About section on mobile only */}
          {isSmallScreen && (
            <div className="space-y-4">
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-3 sm:p-4 lg:p-6">
                  <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-foreground mb-2 sm:mb-3 lg:mb-4">Quick Actions</h2>
                  <div className="space-y-2 sm:space-y-2.5 lg:space-y-3">
                    {content.external_url && (
                      <Button
                        className={`w-full hover:opacity-90 text-white font-semibold ${content.content_type === 'simulation'
                            ? 'bg-gradient-to-r from-blue-500 to-cyan-600'
                            : content.content_type === 'tool'
                              ? 'bg-gradient-to-r from-orange-500 to-amber-600'
                              : content.content_type === 'course'
                                ? 'bg-gradient-to-r from-purple-500 to-pink-600'
                                : content.content_type === 'book'
                                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600'
                                  : 'bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))]'
                          }`}
                        onClick={handleOpenExternal}
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        {getActionButtonText(content.content_type)}
                      </Button>
                    )}
                    <Button
                      className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-semibold"
                      onClick={handleAskBudleeAI}
                    >
                      <Sparkles className="mr-2 h-3 w-3" />
                      Talk to Budlee
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* About Section - Below Title and Description */}
          {isSimulation && (
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-4">
                <Button
                  variant="ghost"
                  onClick={() => setShowDescription(!showDescription)}
                  className="w-full justify-between p-0 h-auto hover:bg-transparent"
                >
                  <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-foreground">About this Simulation</h3>
                  {showDescription ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </Button>
                {showDescription && (
                  <div className="mt-4 space-y-3 text-muted-foreground">
                    <p>{content.description}</p>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-foreground">Features:</h4>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Adjust variables and see results in real-time</li>
                        <li>Experiment with different scenarios</li>
                        <li>Interactive visualizations and animations</li>
                        <li>Data collection and analysis tools</li>
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {!isSimulation && (
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-3 sm:p-4 lg:p-6 space-y-2 sm:space-y-3 lg:space-y-4">
                <h3 className="text-sm sm:text-base lg:text-xl font-semibold text-foreground">About this {content.content_type}</h3>
                <p className="text-muted-foreground leading-relaxed text-xs sm:text-sm lg:text-base">{content.description}</p>

                {content.content_type === 'tool' && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-foreground text-xs sm:text-sm lg:text-base">Features:</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-0.5 sm:space-y-1 ml-2 text-xs sm:text-sm lg:text-base">
                      <li>Interactive calculations and visualizations</li>
                      <li>Step-by-step problem solving</li>
                      <li>Multiple scenarios to explore</li>
                      <li>Instant feedback and explanations</li>
                    </ul>
                  </div>
                )}

                {content.content_type === 'course' && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-foreground text-xs sm:text-sm lg:text-base">What you'll learn:</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-0.5 sm:space-y-1 ml-2 text-xs sm:text-sm lg:text-base">
                      <li>Comprehensive coverage of all topics</li>
                      <li>Video lessons with transcripts</li>
                      <li>Interactive quizzes and assignments</li>
                      <li>Progress tracking and certificates</li>
                    </ul>
                  </div>
                )}

                {content.content_type === 'book' && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-foreground text-xs sm:text-sm lg:text-base">Contents:</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-0.5 sm:space-y-1 ml-2 text-xs sm:text-sm lg:text-base">
                      <li>Detailed explanations of concepts</li>
                      <li>Real-world examples and applications</li>
                      <li>Practice problems with solutions</li>
                      <li>Chapter summaries and key points</li>
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Mobile: Content Info and Related Content - Show below About section on mobile only */}
          {isSmallScreen && (
            <div className="space-y-4">
              {/* Content Info Card */}
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-4">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Content Info</h2>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Type:</span>
                      <span className="ml-2 text-foreground font-medium capitalize">{content.content_type}</span>
                    </div>
                    {content.tag_type && (
                      <div>
                        <span className="text-muted-foreground">Category:</span>
                        <span className="ml-2 text-foreground font-medium">
                          {content.tag_type === 'core' ? 'CORE' : 'GO DEEPER'}
                        </span>
                      </div>
                    )}
                    {content.chapter_detail?.board_name && (
                      <div>
                        <span className="text-muted-foreground">Board:</span>
                        <span className="ml-2 text-foreground font-medium">{content.chapter_detail.board_name}</span>
                      </div>
                    )}
                    {content.chapter_detail?.grade_name && (
                      <div>
                        <span className="text-muted-foreground">Grade:</span>
                        <span className="ml-2 text-foreground font-medium">{content.chapter_detail.grade_name}</span>
                      </div>
                    )}
                    {content.chapter_detail?.subject_name && (
                      <div>
                        <span className="text-muted-foreground">Subject:</span>
                        <span className="ml-2 text-foreground font-medium">{content.chapter_detail.subject_name}</span>
                      </div>
                    )}
                    {content.chapter_detail?.chapter_name && (
                      <div>
                        <span className="text-muted-foreground">Chapter:</span>
                        <span className="ml-2 text-foreground font-medium">{content.chapter_detail.chapter_name}</span>
                      </div>
                    )}
                    {(content.course_duration_hours || content.read_time_minutes) && (
                      <div>
                        <span className="text-muted-foreground">Duration:</span>
                        <span className="ml-2 text-foreground font-medium">
                          {content.course_duration_hours
                            ? `${content.course_duration_hours} hrs`
                            : content.read_time_minutes
                              ? `${content.read_time_minutes} min`
                              : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Related Content Card */}
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-4">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Related Content</h2>
                  <div className="text-sm text-muted-foreground">
                    <p>Related content will be displayed here</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Right Sidebar - Quick Actions (shown when chat panel is closed) */}
      {!showChatPanel && !isSmallScreen && (
        <div className="w-96 bg-[hsl(var(--sidebar-bg))] border-l border-[hsl(var(--card-border))] flex flex-col overflow-y-auto">
          <div className="p-6 space-y-4 pt-24">
            {/* Quick Actions Card */}
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-foreground mb-2 sm:mb-3 lg:mb-4">Quick Actions</h2>
                <div className="space-y-2 sm:space-y-2.5 lg:space-y-3">
                  {content.external_url && (
                    <Button
                      className={`w-full hover:opacity-90 text-white font-semibold ${content.content_type === 'simulation'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-600'
                          : content.content_type === 'tool'
                            ? 'bg-gradient-to-r from-orange-500 to-amber-600'
                            : content.content_type === 'course'
                              ? 'bg-gradient-to-r from-purple-500 to-pink-600'
                              : content.content_type === 'book'
                                ? 'bg-gradient-to-r from-emerald-500 to-teal-600'
                                : 'bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))]'
                        }`}
                      onClick={handleOpenExternal}
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {getActionButtonText(content.content_type)}
                    </Button>
                  )}
                  <Button
                    className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-semibold"
                    onClick={handleAskBudleeAI}
                  >
                    <Sparkles className="mr-2 h-3 w-3" />
                    Talk to Budlee
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Content Info Card */}
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-foreground mb-2 sm:mb-3 lg:mb-4">Content Info</h2>
                <div className="space-y-2 sm:space-y-2.5 lg:space-y-3 text-xs sm:text-sm lg:text-base">
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <span className="ml-2 text-foreground font-medium capitalize">{content.content_type}</span>
                  </div>
                  {content.tag_type && (
                    <div>
                      <span className="text-muted-foreground">Category:</span>
                      <span className="ml-2 text-foreground font-medium">
                        {content.tag_type === 'core' ? 'CORE' : 'GO DEEPER'}
                      </span>
                    </div>
                  )}
                  {content.chapter_detail?.board_name && (
                    <div>
                      <span className="text-muted-foreground">Board:</span>
                      <span className="ml-2 text-foreground font-medium">{content.chapter_detail.board_name}</span>
                    </div>
                  )}
                  {content.chapter_detail?.grade_name && (
                    <div>
                      <span className="text-muted-foreground">Grade:</span>
                      <span className="ml-2 text-foreground font-medium">{content.chapter_detail.grade_name}</span>
                    </div>
                  )}
                  {content.chapter_detail?.subject_name && (
                    <div>
                      <span className="text-muted-foreground">Subject:</span>
                      <span className="ml-2 text-foreground font-medium">{content.chapter_detail.subject_name}</span>
                    </div>
                  )}
                  {content.chapter_detail?.chapter_name && (
                    <div>
                      <span className="text-muted-foreground">Chapter:</span>
                      <span className="ml-2 text-foreground font-medium">{content.chapter_detail.chapter_name}</span>
                    </div>
                  )}
                  {content.course_duration_hours && (
                    <div>
                      <span className="text-muted-foreground">Duration:</span>
                      <span className="ml-2 text-foreground font-medium">{content.course_duration_hours} hours</span>
                    </div>
                  )}
                  {content.read_time_minutes && (
                    <div>
                      <span className="text-muted-foreground">Duration:</span>
                      <span className="ml-2 text-foreground font-medium">{content.read_time_minutes} min</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Related Content Card */}
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-foreground mb-2 sm:mb-3 lg:mb-4">Related Content</h2>
                <div className="text-xs sm:text-sm lg:text-base text-muted-foreground">
                  <p>Related content will be displayed here</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Main content div closing tag */}
      </div>

      {/* Chat Panel - Right Side when Ask Budlee AI is clicked - Fixed Position like CuriosityCentre */}
      {showChatPanel && !isSmallScreen && (
        <div className="fixed right-0 top-0 h-screen w-[400px] z-50">
          <BudleeAIChatPanel
            onClose={handleCloseChatPanel}
            prefilledText={prefilledText}
            onClearPrefilledText={() => setPrefilledText('')}
            pageContext="curiosity_content"
            curiosityContentId={contentId ? parseInt(contentId) : null}
            subjectId={content?.chapter_detail?.subject_id || selectedSubjectId || null}
            chapterId={content?.chapter_detail?.chapter_id || selectedChapterId || null}
          />
        </div>
      )}

      {showChatPanel && isSmallScreen && (
        <div className="fixed inset-x-0 bottom-0 h-1/2 z-50 bg-[hsl(var(--main-bg))] border-t border-[hsl(var(--card-border))]">
          <BudleeAIChatPanel
            onClose={handleCloseChatPanel}
            prefilledText={prefilledText}
            onClearPrefilledText={() => setPrefilledText('')}
            pageContext="curiosity_content"
            curiosityContentId={contentId ? parseInt(contentId) : null}
            subjectId={content?.chapter_detail?.subject_id || selectedSubjectId || null}
            chapterId={content?.chapter_detail?.chapter_id || selectedChapterId || null}
            isMobileSplit={true}
          />
        </div>
      )}

      {/* Floating Budlee AI Mascot - Bottom Right Corner */}
      {!showChatPanel && (
        <BudleeAIIcon
          size="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20"
          iconSize="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10"
          position="fixed bottom-20 sm:bottom-7 right-4 sm:right-6 lg:bottom-8 lg:right-8"
          onClick={handleAskBudleeAI}
          showBadge={true}
        />
      )}
    </div>
  );
}

